import React, { useCallback } from 'react';

import { MicrodataHTMLAttributes, schema } from '@rover/components/src/microdata';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { withOpacity } from '@rover/kibble/utils';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';
import { FORCE_SEO_LANDING } from '@rover/react-lib/src/pages/training/constants/params';
import type { TrainerSearchResult } from '@rover/rsdk/src/apiClient/latest/model/trainerSearchResult';

import Card, { CardLink } from '../Card';
import StaticFeaturedReview from '../CarouselSitterCard/components/StaticFeaturedReview';

import ProfileNameAndPriceRow from './components/ProfileNameAndPriceRow';
import TrainerStatsSection from './components/TrainerStatsSection';

export type Props = {
  searchResult: TrainerSearchResult;
  shouldUsePriceTransparency?: boolean;
  onClick?: () => void;
  onViewIntersect?: (personOpk: TrainerSearchResult['personOpk']) => void;
  rootCarouselRef?: React.RefObject<HTMLElement | null>;
  itemProp?: MicrodataHTMLAttributes['itemProp'];
  locale: Locale;
  currencyCode: string;
};

const TrainingCarouselCard: React.FC<Props> = ({
  searchResult,
  shouldUsePriceTransparency,
  onClick = () => {},
  onViewIntersect = () => {},
  rootCarouselRef,
  itemProp,
  locale,
  currencyCode,
}: Props) => {
  const { colors } = useTheme();
  const {
    personOpk,
    shortName,
    reviewsCount,
    yearsOfExperience,
    city,
    state,
    zip,
    trainerWebUrl,
    trainerExperienceDetails,
    ratingsAverage,
    reviewText,
    reviewDogPhotoUrl: reviewPhotoUrl,
    trainerMethodology,
    trainerMethodologyCustom,
    virtualOnly,
    reviewerShortName,
  } = searchResult;
  const handleCardVisible = useCallback(() => {
    onViewIntersect(personOpk);
  }, [personOpk, onViewIntersect]);

  const cardRef = useOnIntersect(handleCardVisible, true, {
    root: rootCarouselRef?.current,
    threshold: 0.75,
  });

  const seoLandingTrainerWebUrl = `${trainerWebUrl}?${FORCE_SEO_LANDING}=true`;

  return (
    <Flex
      htmlRef={cardRef}
      maxWidth={[300, 350]}
      minWidth={[300, 350]}
      width={[300, 350]}
      sx={{ boxShadow: `0px 1px 4px 0px ${withOpacity(colors.neutral['900'], 0.23)}` }}
      borderRadius="8px"
      position="relative"
      flexDirection="column"
      webProps={{
        itemScope: true,
        itemProp,
        itemType: schema`Person`,
      }}
      height="100%"
    >
      <meta itemProp="url" content={seoLandingTrainerWebUrl} />
      <CardLink
        aria-label={shortName}
        href={seoLandingTrainerWebUrl}
        rel="nofollow"
        target="_self"
        onClick={onClick}
      />
      <Card borderRadius="8px" background="primary" cardStyle="inset" isStacked padding="4x">
        <Flex flexDirection="column" gap="3x">
          <ProfileNameAndPriceRow
            searchResult={searchResult}
            shouldUsePriceTransparency={shouldUsePriceTransparency}
            locale={locale}
            currencyCode={currencyCode}
          />
          <TrainerStatsSection
            reviewsCount={reviewsCount}
            yearsOfExperience={yearsOfExperience}
            ratingsAverage={ratingsAverage}
            city={city}
            state={state}
            zip={zip}
            trainerMethodology={trainerMethodology}
            trainerMethodologyCustom={trainerMethodologyCustom}
            virtualOnly={virtualOnly}
          />
        </Flex>
      </Card>
      <Card
        borderRadius="8px"
        cardStyle="inset"
        isStacked
        paddingX="5x"
        paddingY="4x"
        background="secondary"
        height="100%"
      >
        <StaticFeaturedReview
          reviewText={reviewText ?? ''}
          reviewPhotoUrl={reviewPhotoUrl ?? null}
          description={trainerExperienceDetails}
          reviewerShortName={reviewerShortName ?? ''}
          defaultAvatar="dog"
        />
      </Card>
    </Flex>
  );
};
export default TrainingCarouselCard;
